import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiLoudspeakerDeviceBehaviorPlugin } from '@shure/device-ui/loudspeaker/data-access';
import {
	DeviceUiSettingsFactoryResetBehaviorPlugin,
	FactoryResetText
} from '@shure/device-ui/settings/factory-reset/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { DeviceDbLevel, DeviceDbLevels, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

/** Coverage data per https://shure.jamacloud.com/perspective.req?continue#/items/1056479?projectId=121 */
/* eslint-disable @typescript-eslint/naming-convention */
export const Mxn5dBLevels = {
	[DeviceDbLevel.dB3]: 54,
	[DeviceDbLevel.dB6]: 95,
	[DeviceDbLevel.dB9]: 107
};

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxn5DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		DeviceUiLoudspeakerDeviceBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		CoverageImageProvider
{
	public readonly models: DeviceModel[] = [DeviceModel.MXN5];

	constructor(protected readonly transloco: TranslocoService) {}

	/** Use MXN5-C device image for MXN-5 */
	public getCoverageImage(): CoverageImage {
		return {
			live: 'device-mxn5-c-back.svg',
			virtual: 'device-mxn5-c-back-line.svg',
			isImageRound: true
		};
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(([loudSpeakerLabel, presetsLabel, settingsLabel]) => [
				{
					route: DeviceUiRoute.Loudspeaker,
					label: loudSpeakerLabel
				},
				{
					route: DeviceUiRoute.Presets,
					label: presetsLabel
				},
				{
					route: DeviceUiRoute.Settings,
					label: settingsLabel
				}
			])
		);
	}
	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Loudspeaker;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedWithoutImage;
	}

	public showDanteOutput(): boolean {
		return true;
	}

	public disableLoudspeakerOnPoE(): boolean {
		return false;
	}

	public getLoudspeakerChannelDspSortingMap(): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Delay, 1],
			[DspType.SignalGenerator, 2],
			[DspType.Peq, 3],
			[DspType.Compressor, 4],
			[DspType.Limiter, 5]
		]);
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getDbLevels(): DeviceDbLevels {
		return Mxn5dBLevels;
	}
}
